import { useMemo, useState } from "react";
import { usePersistentState } from "../../../hooks/usePersistentState";
import BackButton from "../../../components/navigation/BackButton";
import type { PaymentRecord, OdemeTipi } from "../../../mocks/payments";
import type { AnimalItem } from "../../../mocks/animals";
import { usePayments } from "../../../hooks/usePayments";
import { useSharePresales } from "../../../hooks/useSharePresales";
import { useBankTransfers } from "../../../hooks/useBankTransfers";
import { normalizeReceiptNo } from "../../../lib/bankTransferStore";
import { getAnimalKgRefMode } from "../../../lib/animalLabels";
import { useAnimals } from "../../../hooks/useAnimals";
import { useCustomers } from "../../../hooks/useCustomers";
import {
  deletePaymentCascade,
  recalcAnimalTotals,
  recalcCustomersFromAnimals,
} from "../../../lib/cascadeOps";
import { exportRowsToCsv, type ExportColumn } from "../../../lib/excelExport";
import {
  matchesAnyOf,
  matchesContains,
  matchesExact,
  compareTextForSort,
  compareNumberForSort,
  applySortDirection,
  type SortDirection,
} from "../../../lib/tableFilters";
import HeaderFilterCell, {
  FilterFooter,
  FilterOptionList,
  FilterSearchInput,
  FilterSortControls,
} from "../../../components/table/HeaderFilterCell";
import PaymentRow, { type EnrichedPaymentRowItem } from "./components/PaymentRow";
import EditPaymentModal from "./components/EditPaymentModal";

const PRESALE_PAYMENT_PREFIX = "Hisse ön satış kapora:";

const fmt = (n: number) =>
  n.toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  });

function splitReceiptNos(value?: string) {
  return String(value ?? "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function getLinkedReceiptNosFromPayment(payment?: Partial<PaymentRecord> | null) {
  if (!payment) return [] as string[];
  const values = [
    ...splitReceiptNos(payment.fisNo),
    ...splitReceiptNos(payment.havaleNo),
  ];

  const description = String(payment.aciklama ?? "");
  const transferLine = description.match(/Havale\s*aktar\s*:\s*([^\n]+)/i)?.[1];
  if (transferLine) {
    values.push(...splitReceiptNos(transferLine));
  }

  return Array.from(
    new Set(
      values
        .map((item) => item.trim())
        .filter(Boolean)
    )
  );
}

interface PaymentHeaderFilters {
  musteri: string;
  telefon: string;
  kupeNo: string;
  hisseNo: string;
  fisNo: string;
  kgRefTipi: string[];
  tip: string[];
  aciklamaTipi: string[];
  dateFrom: string;
  dateTo: string;
}

const EMPTY_FILTERS: PaymentHeaderFilters = {
  musteri: "",
  telefon: "",
  kupeNo: "",
  hisseNo: "",
  fisNo: "",
  kgRefTipi: [],
  tip: [],
  aciklamaTipi: [],
  dateFrom: "",
  dateTo: "",
};

type PaymentSortKey =
  | "tarih"
  | "musteri"
  | "telefon"
  | "kupeNo"
  | "hisseNo"
  | "kgRef"
  | "fisNo"
  | "tutar"
  | "tip"
  | "aciklama";

const PAYMENT_SORT_LABELS: Record<PaymentSortKey, string> = {
  tarih: "Tarih",
  musteri: "Müşteri",
  telefon: "Telefon",
  kupeNo: "Küpe",
  hisseNo: "Hisse",
  kgRef: "KG/REF Fiy.",
  fisNo: "Fiş No",
  tutar: "Tutar",
  tip: "Tip",
  aciklama: "Açıklama",
};

function comparePaymentsByKey(a: EnrichedPaymentRowItem, b: EnrichedPaymentRowItem, key: PaymentSortKey) {
  if (key === "tutar") return compareNumberForSort(a.tutar, b.tutar);
  if (key === "musteri") return compareTextForSort(a.musteriAdi, b.musteriAdi);
  if (key === "telefon") return compareTextForSort(a.telefon, b.telefon);
  if (key === "kupeNo") return compareTextForSort(a.hayvanKupeNo, b.hayvanKupeNo);
  if (key === "hisseNo") return compareTextForSort(a.hisseNo, b.hisseNo);
  if (key === "kgRef") return compareTextForSort(a.kgRefText, b.kgRefText);
  if (key === "fisNo") return compareTextForSort(a.fisNo, b.fisNo);
  if (key === "tip") return compareTextForSort(a.tip, b.tip);
  if (key === "aciklama") return compareTextForSort(a.aciklama, b.aciklama);
  return compareTextForSort(a.tarih, b.tarih);
}

interface ExportPaymentRow {
  tarih: string;
  musteri: string;
  telefon: string;
  kupeNo: string;
  hisseNo: string;
  kgRef: string;
  fisNo: string;
  tip: string;
  tutar: number;
  aciklama: string;
}

function applyPaymentToAnimals(
  animals: AnimalItem[],
  payment: PaymentRecord
): AnimalItem[] {
  return animals.map((animal) => {
    if (animal.id !== payment.animalId) return animal;

    const hasTargetShare = animal.hisseler.some(
      (share) => share.id === payment.shareId
    );
    if (!hasTargetShare) return animal;

    const nextAnimal: AnimalItem = {
      ...animal,
      hisseler: animal.hisseler.map((share) => {
        if (share.id !== payment.shareId) return share;

        return {
          ...share,
          odenen: share.odenen + payment.tutar,
        };
      }),
    };

    return recalcAnimalTotals(nextAnimal);
  });
}

function buildKgRefText(animal?: AnimalItem | null) {
  if (!animal) return "";
  const kg = animal.kg ? `${animal.kg} kg` : "—";
  const ref = animal.referansFiyat ? `${animal.referansFiyat} ₺` : "—";
  return `${kg} / ${ref}`;
}


function getPresaleSaleNoFromPayment(payment?: Partial<PaymentRecord> | null) {
  if (!payment) return "";
  const description = String(payment.aciklama ?? "");
  const match = description.match(/Hisse\s*ön\s*satış\s*kapora:\s*(HS-[^\s-]+-[^\s]+)/i);
  if (match?.[1]) return match[1];
  const hisseNo = String(payment.hisseNo ?? "").trim();
  return hisseNo.startsWith("HS-") ? hisseNo : "";
}

function getPaymentDescriptionType(payment: Pick<PaymentRecord, "aciklama">) {
  const description = String(payment.aciklama ?? "").trim().toLocaleLowerCase("tr-TR");

  if (description.includes("satış sırasında kapora") || description.includes("satis sirasinda kapora")) {
    return "Satış sırasında kapora";
  }

  if (description.includes("ödeme al ekranından kayıt") || description.includes("odeme al ekranindan kayit")) {
    return "Ödeme Al ekranından kayıt";
  }

  return "Diğerleri";
}

export default function OdemelerPage() {
  const { payments: data, setAllPayments } = usePayments();
  const { setSharePresales } = useSharePresales();
  const { animals, setAllAnimals } = useAnimals();
  const { customers, setAllCustomers } = useCustomers();
  const { setAllBankTransfers } = useBankTransfers();

  const [filters, setFilters] = usePersistentState<PaymentHeaderFilters>("ec.filters.odemeler.v2", EMPTY_FILTERS);
  const [sortKey, setSortKey] = usePersistentState<PaymentSortKey>("ec.sort.odemeler.key.v1", "tarih");
  const [sortDir, setSortDir] = usePersistentState<SortDirection>("ec.sort.odemeler.dir.v1", "desc");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<PaymentRecord | null | undefined>(
    undefined
  );
  const [deleteTarget, setDeleteTarget] = useState<PaymentRecord | null>(null);

  const enrichedData = useMemo<EnrichedPaymentRowItem[]>(() => {
    return data.map((payment) => {
      const animal = animals.find((item) => item.id === payment.animalId) ?? null;
      return {
        ...payment,
        fisNo: payment.fisNo,
        kgRefText: buildKgRefText(animal),
        kgRefMode: animal ? getAnimalKgRefMode(animal) : "Normal Satış",
        aciklamaTipi: getPaymentDescriptionType(payment),
      };
    });
  }, [animals, data]);

  const filtered = useMemo(() => {
    return enrichedData.filter((payment) => {
      if (filters.dateFrom && payment.tarih < filters.dateFrom) return false;
      if (filters.dateTo && payment.tarih > filters.dateTo) return false;
      if (!matchesContains(payment.musteriAdi, filters.musteri)) return false;
      if (!matchesContains(payment.telefon.replace(/\s/g, ""), filters.telefon.replace(/\s/g, ""))) {
        return false;
      }
      if (!matchesExact(payment.hayvanKupeNo, filters.kupeNo)) return false;
      if (!matchesExact(payment.hisseNo, filters.hisseNo)) return false;
      if (!matchesContains(payment.fisNo ?? "", filters.fisNo)) return false;
      if (!matchesAnyOf(payment.kgRefMode ?? "Normal Satış", filters.kgRefTipi)) return false;
      if (!matchesAnyOf(payment.tip, filters.tip)) return false;
      if (!matchesAnyOf(payment.aciklamaTipi ?? "Diğerleri", filters.aciklamaTipi)) return false;
      return true;
    });
  }, [enrichedData, filters]);

  const totalTutar = filtered.reduce((sum, payment) => sum + payment.tutar, 0);
  const nakitTutar = filtered
    .filter((payment) => payment.tip === "Nakit")
    .reduce((sum, payment) => sum + payment.tutar, 0);
  const havaleTutar = filtered
    .filter((payment) => payment.tip === "Havale")
    .reduce((sum, payment) => sum + payment.tutar, 0);

  const nakitCount = filtered.filter((payment) => payment.tip === "Nakit").length;
  const havaleCount = filtered.filter((payment) => payment.tip === "Havale").length;

  function exportCurrent() {
    const rows: ExportPaymentRow[] = filtered.map((payment) => ({
      tarih: payment.tarih,
      musteri: payment.musteriAdi,
      telefon: payment.telefon,
      kupeNo: payment.hayvanKupeNo,
      hisseNo: payment.hisseNo,
      kgRef: payment.kgRefText ?? "",
      fisNo: payment.fisNo ?? "",
      tip: payment.tip,
      tutar: payment.tutar,
      aciklama: payment.aciklama ?? "",
    }));

    const columns: ExportColumn<ExportPaymentRow>[] = [
      { key: "tarih", label: "Tarih" },
      { key: "musteri", label: "Müşteri" },
      { key: "telefon", label: "Telefon" },
      { key: "kupeNo", label: "Küpe No" },
      { key: "hisseNo", label: "Hisse" },
      { key: "kgRef", label: "KG/REF Fiy." },
      { key: "fisNo", label: "Fiş No" },
      { key: "tip", label: "Tip" },
      { key: "tutar", label: "Tutar" },
      { key: "aciklama", label: "Açıklama" },
    ];

    exportRowsToCsv(
      `odemeler-${new Date().toISOString().slice(0, 10)}`,
      rows,
      columns
    );
  }

  function applyBankTransferChanges(changes?: { linkedIds?: string[]; unlinkedFisNos?: string[] }) {
    const linkedIds = new Set(changes?.linkedIds ?? []);
    const unlinkedFisNos = new Set((changes?.unlinkedFisNos ?? []).map(normalizeReceiptNo));
    if (linkedIds.size === 0 && unlinkedFisNos.size === 0) return;

    void setAllBankTransfers((prev) =>
      prev.map((item) => {
        if (linkedIds.has(item.id)) {
          return { ...item, automaticFilled: true, updatedAt: new Date().toISOString() };
        }
        if (unlinkedFisNos.has(normalizeReceiptNo(item.fisNo))) {
          return { ...item, automaticFilled: false, updatedAt: new Date().toISOString() };
        }
        return item;
      })
    );
  }

  function handleSave(formData: Partial<PaymentRecord>, bankTransferChanges?: { linkedIds?: string[]; unlinkedFisNos?: string[] }) {
    const musteriId = formData.musteriId ?? 0;
    const animalId = formData.animalId ?? 0;
    const shareId = formData.shareId ?? 0;
    const tutar = Number(formData.tutar ?? 0);

    const presaleSaleNo = editTarget ? getPresaleSaleNoFromPayment(editTarget) : "";

    if (presaleSaleNo && editTarget) {
      const updatedPayment: PaymentRecord = {
        ...editTarget,
        musteriId,
        tarih: formData.tarih ?? editTarget.tarih,
        musteriAdi: formData.musteriAdi ?? editTarget.musteriAdi,
        telefon: formData.telefon ?? editTarget.telefon,
        tutar,
        tip: formData.tip ?? editTarget.tip,
        aciklama: formData.aciklama ?? editTarget.aciklama,
        havaleNo: formData.tip === "Havale" ? formData.havaleNo : undefined,
        fisNo: formData.tip === "Havale" ? formData.fisNo || formData.havaleNo : undefined,
        olusturan: formData.olusturan ?? editTarget.olusturan,
      };
      if (Number(editTarget.animalId ?? 0) > 0 && Number(editTarget.shareId ?? 0) > 0) {
        const rollback = deletePaymentCascade({ paymentId: editTarget.id, animals, customers, payments: data });
        const nextAnimals = applyPaymentToAnimals(rollback.animals, updatedPayment);
        const nextCustomers = recalcCustomersFromAnimals(rollback.customers, nextAnimals);
        const nextPayments = [updatedPayment, ...rollback.payments].sort((a, b) => b.tarih.localeCompare(a.tarih) || b.id - a.id);
        setAllAnimals(() => nextAnimals);
        setAllCustomers(() => nextCustomers);
        setAllPayments(() => nextPayments);
      } else {
        setAllPayments((prev) => prev.map((item) => item.id === editTarget.id ? updatedPayment : item));
      }
      setSharePresales((prev) => ({
        ...prev,
        sales: prev.sales.map((sale) =>
          sale.satisNo === presaleSaleNo
            ? {
                ...sale,
                musteriId: musteriId || sale.musteriId,
                musteriAdi: updatedPayment.musteriAdi,
                telefon: updatedPayment.telefon,
                kaporaTutar: tutar,
                kalanTutar: Math.max(0, sale.toplamTutar - tutar),
                odemeTipi: updatedPayment.tip,
                havaleNo: updatedPayment.tip === "Havale" ? updatedPayment.havaleNo : undefined,
                updatedAt: new Date().toISOString(),
              }
            : sale
        ),
      }));
      applyBankTransferChanges(bankTransferChanges);
      setEditTarget(undefined);
      return;
    }

    if (!musteriId || !animalId || !shareId || !tutar || tutar <= 0) {
      window.alert("Ödeme kaydı için müşteri, hayvan, hisse ve tutar zorunludur.");
      return;
    }

    if (editTarget === null) {
      const newId = data.length > 0 ? Math.max(...data.map((p) => p.id)) + 1 : 1;

      const newPayment: PaymentRecord = {
        id: newId,
        musteriId,
        animalId,
        shareId,
        tarih: formData.tarih ?? new Date().toISOString().slice(0, 10),
        musteriAdi: formData.musteriAdi ?? "",
        telefon: formData.telefon ?? "",
        hayvanKupeNo: formData.hayvanKupeNo ?? "",
        hisseNo: formData.hisseNo ?? "",
        tutar,
        tip: formData.tip ?? "Nakit",
        aciklama: formData.aciklama ?? "",
        havaleNo: formData.tip === "Havale" ? formData.havaleNo : undefined,
        fisNo: formData.tip === "Havale" ? formData.fisNo || formData.havaleNo : undefined,
        olusturan: formData.olusturan ?? "Admin",
      };

      const nextAnimals = applyPaymentToAnimals(animals, newPayment);
      const nextCustomers = recalcCustomersFromAnimals(customers, nextAnimals);
      const nextPayments = [newPayment, ...data];

      setAllAnimals(() => nextAnimals);
      setAllCustomers(() => nextCustomers);
      setAllPayments(() => nextPayments);
      applyBankTransferChanges(bankTransferChanges);
      setEditTarget(undefined);
      return;
    }

    if (editTarget) {
      const rollback = deletePaymentCascade({
        paymentId: editTarget.id,
        animals,
        customers,
        payments: data,
      });

      const updatedPayment: PaymentRecord = {
        id: editTarget.id,
        musteriId,
        animalId,
        shareId,
        tarih: formData.tarih ?? editTarget.tarih,
        musteriAdi: formData.musteriAdi ?? editTarget.musteriAdi,
        telefon: formData.telefon ?? editTarget.telefon,
        hayvanKupeNo: formData.hayvanKupeNo ?? editTarget.hayvanKupeNo,
        hisseNo: formData.hisseNo ?? editTarget.hisseNo,
        tutar,
        tip: formData.tip ?? editTarget.tip,
        aciklama: formData.aciklama ?? "",
        havaleNo: formData.tip === "Havale" ? formData.havaleNo : undefined,
        fisNo: formData.tip === "Havale" ? formData.fisNo || formData.havaleNo : undefined,
        olusturan: formData.olusturan ?? editTarget.olusturan,
      };

      const nextAnimals = applyPaymentToAnimals(rollback.animals, updatedPayment);
      const nextCustomers = recalcCustomersFromAnimals(
        rollback.customers,
        nextAnimals
      );
      const nextPayments = [updatedPayment, ...rollback.payments].sort(
        (a, b) => b.tarih.localeCompare(a.tarih) || b.id - a.id
      );

      setAllAnimals(() => nextAnimals);
      setAllCustomers(() => nextCustomers);
      setAllPayments(() => nextPayments);
      applyBankTransferChanges(bankTransferChanges);
      setEditTarget(undefined);
    }
  }

  function handleDelete() {
    if (!deleteTarget) return;

    const presaleSaleNo = getPresaleSaleNoFromPayment(deleteTarget);

    if (presaleSaleNo) {
      const now = new Date().toISOString();
      if (Number(deleteTarget.animalId ?? 0) > 0 && Number(deleteTarget.shareId ?? 0) > 0) {
        const result = deletePaymentCascade({ paymentId: deleteTarget.id, animals, customers, payments: data });
        setAllAnimals(() => result.animals);
        setAllCustomers(() => result.customers);
        setAllPayments(() => result.payments);
      } else {
        setAllPayments((prev) => prev.filter((item) => item.id !== deleteTarget.id));
      }
      setSharePresales((prev) => ({
        ...prev,
        sales: prev.sales.map((sale) =>
          sale.satisNo === presaleSaleNo
            ? {
                ...sale,
                kaporaTutar: 0,
                kalanTutar: sale.toplamTutar,
                odemeTipi: "Nakit",
                havaleNo: undefined,
                linkedBankTransferIds: [],
                updatedAt: now,
              }
            : sale
        ),
      }));
      applyBankTransferChanges({ unlinkedFisNos: getLinkedReceiptNosFromPayment(deleteTarget) });
      setDeleteTarget(null);
      return;
    }

    const result = deletePaymentCascade({
      paymentId: deleteTarget.id,
      animals,
      customers,
      payments: data,
    });

    setAllAnimals(() => result.animals);
    setAllCustomers(() => result.customers);
    setAllPayments(() => result.payments);
    applyBankTransferChanges({ unlinkedFisNos: getLinkedReceiptNosFromPayment(deleteTarget) });
    setDeleteTarget(null);
  }

  function renderSortControls(key: PaymentSortKey, ascLabel?: string, descLabel?: string) {
    return (
      <FilterSortControls
        activeDirection={sortKey === key ? sortDir : null}
        ascLabel={ascLabel}
        descLabel={descLabel}
        onSort={(direction) => {
          setSortKey(key);
          setSortDir(direction);
        }}
      />
    );
  }

  const sorted = [...filtered].sort((a, b) => {
    const result = comparePaymentsByKey(a, b, sortKey);
    if (result !== 0) return applySortDirection(result, sortDir);
    return b.id - a.id;
  });

  const hasActiveFilters = Object.values(filters).some((value) =>
    Array.isArray(value) ? value.length > 0 : Boolean(value)
  );

  return (
    <div className="min-h-screen bg-farm-cream font-body">
      <div className="max-w-7xl mx-auto px-3 sm:px-5 py-4">
        <div className="lg:sticky lg:top-0 z-40 -mx-3 border-b border-farm-border/70 bg-farm-cream/95 px-3 pb-3 pt-1 shadow-sm backdrop-blur sm:-mx-5 sm:px-5">
          <div className="mb-3 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-heading font-bold text-base text-farm-brown">
              Ödemeler
            </h1>
            <p className="font-body text-xs text-farm-muted">
              Başlığa basarak filtreleyin. Küpe, hisse ve fiş tam eşleşir.
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
          <BackButton />
            {hasActiveFilters ? (
              <button
                type="button"
                onClick={() => setFilters(EMPTY_FILTERS)}
                className="px-3 py-2 rounded-full border border-farm-border bg-white text-farm-muted text-xs font-semibold hover:bg-farm-cream"
              >
                Filtreleri Temizle
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setMobileFiltersOpen((prev) => !prev)}
              className="lg:hidden px-3 py-2 rounded-full border border-farm-border bg-white text-farm-brown text-xs font-semibold hover:bg-farm-cream"
            >
              <i className="ri-filter-3-line mr-1" />
              Mobil Filtre
            </button>
            <button
              type="button"
              onClick={() => setEditTarget(null)}
              className="px-3 py-2 rounded-full bg-farm-earth text-white text-xs font-semibold hover:bg-farm-brown"
            >
              Yeni Ödeme Kaydı
            </button>
            <button
              type="button"
              onClick={exportCurrent}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-farm-border bg-white text-farm-brown text-xs font-semibold hover:border-farm-earth/40"
            >
              <i className="ri-file-excel-2-line text-base" />
              Excel Çıktısı
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-3">
          {[
            {
              label: "Toplam Tutar",
              value: fmt(totalTutar),
              icon: "ri-money-dollar-circle-line",
              color: "text-farm-green",
              sub: `${sorted.length} kayıt`,
            },
            {
              label: "Nakit",
              value: fmt(nakitTutar),
              icon: "ri-wallet-3-line",
              color: "text-farm-green",
              sub: `${nakitCount} ödeme`,
            },
            {
              label: "Havale",
              value: fmt(havaleTutar),
              icon: "ri-bank-line",
              color: "text-sky-600",
              sub: `${havaleCount} ödeme`,
            },
            {
              label: "Toplam Kayıt",
              value: String(data.length),
              icon: "ri-file-list-3-line",
              color: "text-farm-brown",
              sub: "tüm zamanlar",
            },
          ].map((item) => (
            <div
              key={item.label}
              className="bg-farm-warm border border-farm-border rounded-farm px-3 py-2.5 flex items-center gap-2"
            >
              <div
                className={`w-7 h-7 flex items-center justify-center rounded-lg bg-farm-cream ${item.color} flex-shrink-0`}
              >
                <i className={`${item.icon} text-sm`} />
              </div>
              <div className="min-w-0">
                <p className={`font-body font-bold text-sm leading-none truncate ${item.color}`}>
                  {item.value}
                </p>
                <p className="font-body text-[10px] text-farm-muted mt-0.5 leading-none">
                  {item.label}
                </p>
                <p className="font-body text-[10px] text-farm-muted/60 leading-none">
                  {item.sub}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mb-3 grid grid-cols-1 gap-2 rounded-farm border border-farm-border bg-white p-3 md:grid-cols-3">
          <input
            value={filters.kupeNo}
            onChange={(event) => setFilters((prev) => ({ ...prev, kupeNo: event.target.value }))}
            placeholder="Küpe No ara"
            className="h-11 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none focus:border-farm-earth"
          />
          <input
            value={filters.fisNo}
            onChange={(event) => setFilters((prev) => ({ ...prev, fisNo: event.target.value }))}
            placeholder="Fiş No ara"
            className="h-11 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none focus:border-farm-earth"
          />
          <input
            value={filters.musteri}
            onChange={(event) => setFilters((prev) => ({ ...prev, musteri: event.target.value }))}
            placeholder="Ad Soyad ara"
            className="h-11 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none focus:border-farm-earth"
          />
        </div>

        {mobileFiltersOpen && (
          <div className="lg:hidden rounded-farm border border-farm-border bg-white p-3 mb-3 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <FilterSearchInput value={filters.musteri} onChange={(value) => setFilters((prev) => ({ ...prev, musteri: value }))} placeholder="Müşteri ara" />
              <FilterSearchInput value={filters.telefon} onChange={(value) => setFilters((prev) => ({ ...prev, telefon: value }))} placeholder="Telefon ara" />
              <FilterSearchInput value={filters.kupeNo} onChange={(value) => setFilters((prev) => ({ ...prev, kupeNo: value }))} placeholder="Tam küpe no" />
              <FilterSearchInput value={filters.hisseNo} onChange={(value) => setFilters((prev) => ({ ...prev, hisseNo: value }))} placeholder="Tam hisse" />
              <FilterSearchInput value={filters.fisNo} onChange={(value) => setFilters((prev) => ({ ...prev, fisNo: value }))} placeholder="Tam fiş no" />
              <select value={filters.tip[0] ?? ""} onChange={(e) => setFilters((prev) => ({ ...prev, tip: e.target.value ? [e.target.value] : [] }))} className="h-10 rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none focus:border-farm-earth">
                <option value="">Tip: Tümü</option>
                <option value="Nakit">Nakit</option>
                <option value="Havale">Havale</option>
              </select>
              <select value={filters.kgRefTipi[0] ?? ""} onChange={(e) => setFilters((prev) => ({ ...prev, kgRefTipi: e.target.value ? [e.target.value] : [] }))} className="h-10 rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none focus:border-farm-earth">
                <option value="">KG/REF: Tümü</option>
                <option value="KG Satış">KG Satış</option>
                <option value="Normal Satış">Normal Satış</option>
              </select>
              <select value={filters.aciklamaTipi[0] ?? ""} onChange={(e) => setFilters((prev) => ({ ...prev, aciklamaTipi: e.target.value ? [e.target.value] : [] }))} className="h-10 rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none focus:border-farm-earth">
                <option value="">Açıklama: Tümü</option>
                <option value="Satış sırasında kapora">Satış sırasında kapora</option>
                <option value="Ödeme Al ekranından kayıt">Ödeme Al ekranından kayıt</option>
                <option value="Diğerleri">Diğerleri</option>
              </select>
            </div>
            <div className="grid grid-cols-[1fr_auto] gap-2">
              <select value={sortKey} onChange={(e) => setSortKey(e.target.value as PaymentSortKey)} className="h-10 rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none focus:border-farm-earth">
                {(Object.keys(PAYMENT_SORT_LABELS) as PaymentSortKey[]).map((key) => (
                  <option key={key} value={key}>Sırala: {PAYMENT_SORT_LABELS[key]}</option>
                ))}
              </select>
              <button type="button" onClick={() => setSortDir((prev) => (prev === "asc" ? "desc" : "asc"))} className="h-10 rounded-xl border border-farm-border bg-white px-3 text-sm font-semibold text-farm-brown">
                {sortDir === "asc" ? "Artan" : "Azalan"}
              </button>
            </div>
            <button type="button" onClick={() => setFilters(EMPTY_FILTERS)} className="w-full h-10 rounded-xl border border-farm-border text-sm font-semibold text-farm-muted">Filtreleri Temizle</button>
          </div>
        )}

        <div className="bg-farm-warm border border-farm-border border-b-0 rounded-t-farm overflow-visible">
          <div
            className="hidden min-w-[1180px] bg-farm-cream lg:grid border-b border-farm-border text-[10px] font-body font-semibold text-farm-muted uppercase tracking-wide shadow-sm"
            style={{
              gridTemplateColumns: "82px 150px 120px 88px 88px 110px 88px 88px 68px 120px 66px",
              minWidth: "1180px",
            }}
          >
            <HeaderFilterCell label="Tarih" active={Boolean(filters.dateFrom || filters.dateTo) || sortKey === "tarih"}>
              {renderSortControls("tarih", "Eskiden yeniye", "Yeniden eskiye")}
              <div className="space-y-2">
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(event) =>
                    setFilters((prev) => ({ ...prev, dateFrom: event.target.value }))
                  }
                  className="h-10 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none focus:border-farm-earth"
                />
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(event) =>
                    setFilters((prev) => ({ ...prev, dateTo: event.target.value }))
                  }
                  className="h-10 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none focus:border-farm-earth"
                />
              </div>
              <FilterFooter
                onClear={() =>
                  setFilters((prev) => ({ ...prev, dateFrom: "", dateTo: "" }))
                }
                hint="Aralık"
              />
            </HeaderFilterCell>

            <HeaderFilterCell label="Müşteri" active={Boolean(filters.musteri) || sortKey === "musteri"}>
              {renderSortControls("musteri")}
              <FilterSearchInput
                value={filters.musteri}
                onChange={(value) => setFilters((prev) => ({ ...prev, musteri: value }))}
                placeholder="Ad soyad"
              />
              <FilterFooter onClear={() => setFilters((prev) => ({ ...prev, musteri: "" }))} />
            </HeaderFilterCell>

            <HeaderFilterCell label="Telefon" active={Boolean(filters.telefon) || sortKey === "telefon"}>
              {renderSortControls("telefon")}
              <FilterSearchInput
                value={filters.telefon}
                onChange={(value) => setFilters((prev) => ({ ...prev, telefon: value }))}
                placeholder="Telefon"
              />
              <FilterFooter onClear={() => setFilters((prev) => ({ ...prev, telefon: "" }))} />
            </HeaderFilterCell>

            <HeaderFilterCell label="Küpe" active={Boolean(filters.kupeNo) || sortKey === "kupeNo"}>
              {renderSortControls("kupeNo")}
              <FilterSearchInput
                value={filters.kupeNo}
                onChange={(value) => setFilters((prev) => ({ ...prev, kupeNo: value }))}
                placeholder="Tam küpe no"
              />
              <FilterFooter onClear={() => setFilters((prev) => ({ ...prev, kupeNo: "" }))} hint="Tam eşleşme" />
            </HeaderFilterCell>

            <HeaderFilterCell label="Hisse" active={Boolean(filters.hisseNo) || sortKey === "hisseNo"}>
              {renderSortControls("hisseNo")}
              <FilterSearchInput
                value={filters.hisseNo}
                onChange={(value) => setFilters((prev) => ({ ...prev, hisseNo: value }))}
                placeholder="Tam hisse"
              />
              <FilterFooter onClear={() => setFilters((prev) => ({ ...prev, hisseNo: "" }))} hint="Tam eşleşme" />
            </HeaderFilterCell>

            <HeaderFilterCell label="KG/REF Fiy." active={filters.kgRefTipi.length > 0 || sortKey === "kgRef"}>
              {renderSortControls("kgRef")}
              <FilterOptionList
                options={["KG Satış", "Normal Satış"]}
                selected={filters.kgRefTipi}
                onToggle={(value) =>
                  setFilters((prev) => ({
                    ...prev,
                    kgRefTipi: prev.kgRefTipi.includes(value)
                      ? prev.kgRefTipi.filter((item) => item !== value)
                      : [...prev.kgRefTipi, value],
                  }))
                }
              />
              <FilterFooter onClear={() => setFilters((prev) => ({ ...prev, kgRefTipi: [] }))} hint="KG Satış / Normal Satış" />
            </HeaderFilterCell>

            <HeaderFilterCell label="Fiş No" active={Boolean(filters.fisNo) || sortKey === "fisNo"}>
              {renderSortControls("fisNo")}
              <FilterSearchInput
                value={filters.fisNo}
                onChange={(value) => setFilters((prev) => ({ ...prev, fisNo: value }))}
                placeholder="Tam fiş no"
              />
              <FilterFooter onClear={() => setFilters((prev) => ({ ...prev, fisNo: "" }))} hint="İçerir araması" />
            </HeaderFilterCell>

            <HeaderFilterCell label="Tutar" active={sortKey === "tutar"} align="right">
              {renderSortControls("tutar", "Küçükten büyüğe", "Büyükten küçüğe")}
              <div className="text-[11px] text-farm-muted">Tutar için sıralama kullanılabilir.</div>
            </HeaderFilterCell>

            <HeaderFilterCell label="Tip" active={filters.tip.length > 0 || sortKey === "tip"}>
              {renderSortControls("tip")}
              <FilterOptionList
                options={["Nakit", "Havale"]}
                selected={filters.tip}
                onToggle={(value) =>
                  setFilters((prev) => ({
                    ...prev,
                    tip: prev.tip.includes(value)
                      ? prev.tip.filter((item) => item !== value)
                      : [...prev.tip, value],
                  }))
                }
              />
              <FilterFooter onClear={() => setFilters((prev) => ({ ...prev, tip: [] }))} />
            </HeaderFilterCell>

            <HeaderFilterCell label="Açıklama" active={filters.aciklamaTipi.length > 0 || sortKey === "aciklama"} align="right">
              {renderSortControls("aciklama")}
              <FilterOptionList
                options={["Satış sırasında kapora", "Ödeme Al ekranından kayıt", "Diğerleri"]}
                selected={filters.aciklamaTipi}
                onToggle={(value) =>
                  setFilters((prev) => ({
                    ...prev,
                    aciklamaTipi: prev.aciklamaTipi.includes(value)
                      ? prev.aciklamaTipi.filter((item) => item !== value)
                      : [...prev.aciklamaTipi, value],
                  }))
                }
              />
              <FilterFooter onClear={() => setFilters((prev) => ({ ...prev, aciklamaTipi: [] }))} hint="Otomatik açıklama türü" />
            </HeaderFilterCell>

            <div className="px-2 py-2">İşlem</div>
          </div>
        </div>
      </div>

        <div className="bg-farm-warm border border-farm-border border-t-0 rounded-b-farm overflow-visible">
          {sorted.length === 0 ? (
            <div className="py-12 text-center">
              <div className="w-10 h-10 flex items-center justify-center mx-auto text-farm-muted mb-2">
                <i className="ri-search-line text-3xl" />
              </div>
              <p className="font-body text-sm text-farm-muted">
                Ödeme kaydı bulunamadı
              </p>
              <p className="font-body text-xs text-farm-muted/60 mt-1">
                Filtreleri değiştirin veya yeni kayıt ekleyin
              </p>
            </div>
          ) : (
            sorted.map((payment) => (
              <PaymentRow
                key={payment.id}
                payment={payment}
                nameHighlight={filters.musteri}
                phoneHighlight={filters.telefon}
                onEdit={() => setEditTarget(payment)}
                onDelete={() => setDeleteTarget(payment)}
              />
            ))
          )}

          {sorted.length > 0 && (
            <div
              className="hidden min-w-[1180px] bg-farm-cream lg:grid border-t border-farm-border text-xs font-body font-semibold text-farm-brown"
              style={{
                gridTemplateColumns: "82px 150px 120px 88px 88px 110px 88px 88px 68px 120px 66px",
                minWidth: "1180px",
              }}
            >
              <div className="px-2 py-2 col-span-7 text-farm-muted font-normal">
                {sorted.length} kayıt gösteriliyor
              </div>
              <div className="px-2 py-2 text-right text-farm-green font-bold">
                {fmt(totalTutar)}
              </div>
              <div className="px-2 py-2 col-span-3" />
            </div>
          )}
        </div>

        {sorted.length > 0 && (
          <div className="lg:hidden mt-2 flex items-center justify-between px-3 py-2 bg-farm-warm border border-farm-border rounded-farm">
            <span className="text-xs text-farm-muted">{sorted.length} kayıt</span>
            <span className="text-sm font-bold text-farm-green">
              {fmt(totalTutar)}
            </span>
          </div>
        )}
      </div>

      {editTarget !== undefined && (
        <EditPaymentModal
          payment={editTarget}
          onSave={handleSave}
          onClose={() => setEditTarget(undefined)}
        />
      )}

      {deleteTarget !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-xl p-5 w-full max-w-xs">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-100 mx-auto mb-3">
              <i className="ri-delete-bin-line text-red-500 text-xl" />
            </div>

            <p className="text-sm font-semibold text-gray-800 text-center mb-1">
              Ödeme Silinsin mi?
            </p>
            <p className="text-xs text-gray-500 text-center mb-4">
              Bu ödeme silinecek. Hisse ödenen/kalan tutarı güncellenecek.
            </p>

            <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-3 mb-4 text-xs text-gray-700 space-y-1">
              <p>
                <span className="font-semibold">Müşteri:</span>{" "}
                {deleteTarget.musteriAdi}
              </p>
              <p>
                <span className="font-semibold">Hisse:</span>{" "}
                {deleteTarget.hisseNo}
              </p>
              <p>
                <span className="font-semibold">Tutar:</span>{" "}
                {fmt(deleteTarget.tutar)}
              </p>
              <p>
                <span className="font-semibold">Tarih:</span>{" "}
                {deleteTarget.tarih}
              </p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                type="button"
                className="flex-1 py-2 text-sm border-2 border-gray-200 text-gray-600 font-medium rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors"
              >
                İptal
              </button>
              <button
                onClick={handleDelete}
                type="button"
                className="flex-1 py-2 text-sm bg-red-500 hover:bg-red-600 text-white font-medium rounded-lg cursor-pointer whitespace-nowrap transition-colors"
              >
                Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
